/**
 * ⛔⛔ THE ONLY QUESTION THAT MATCHES THE HAZARD: **MIGHT WRITES HAVE HAPPENED?**
 *
 * This classifier has been corrected three times and every correction moved a boundary inside the
 * WRONG PARTITION:
 *   round 1  everything ambiguous -> "might still be running"        (too broad)
 *   round 2  everything but 502/504 -> "confirmed rejection"          (too narrow)
 *   round 3  every non-2xx -> unknown                                 (right, and still incomplete)
 * ⇒ 🔑🔑 ALL THREE PARTITIONED ON THE **STATUS CODE**, AND THE HAZARD IS ORTHOGONAL TO IT. The two
 * findings that survived round 3 are both on **2xx**: a 200 whose body will not parse, and a 200
 * carrying the backend's own `success:false, totalCreated>0, hasErrors:true` — an explicit partial
 * write. A 200 was never in the set being classified, so no amount of moving the boundary could
 * reach them.
 *
 * ⇒ SO THE PARTITION IS REDRAWN ON THE ACTUAL QUESTION, AND THE DEFAULT IS LOCKED:
 *
 *   wrote      rows definitely written — `totalCreated > 0`, SUCCESS OR NOT      -> LOCKED
 *   no-op      it ran and wrote nothing (`wroteNothing`)                          -> enabled
 *   rejected   a PROVEN pre-execution refusal (401 before the route handler)      -> enabled
 *   unknown    everything else: 5xx, gateway, transport, unreadable 2xx body      -> LOCKED
 *
 * ⭐ "ENABLED" IS THE BRANCH THAT MUST BE EARNED. Over-locking costs a page reload. Over-enabling
 * costs a duplicate expensive AI job, and we have measured what that job does: 369 rows on a request
 * that did not return success.
 *
 * ⚠️ AND THE 401 EXCEPTION IS THE ONE BRANCH THAT SURVIVED ALL THREE ROUNDS — because it was grounded
 * in the MIDDLEWARE (`authenticateJWT` runs before the route handler, so no write stage can have
 * executed) rather than in the status number. That is the method the rest of this file now uses:
 * DERIVE THE CLAIM FROM WHAT THE SERVER DID, NOT FROM WHAT THE WIRE SAID.
 */

export type PopulateOutcome =
  /** Rows were definitely written — whether or not the run reported success. */
  | { kind: 'wrote'; totalCreated: number; clean: boolean }
  /** It ran to completion and wrote nothing. Re-running is safe. */
  | { kind: 'no-op' }
  /** An explicit, pre-execution refusal: the handler never ran. */
  | { kind: 'rejected'; reason: string }
  /**
   * No trustworthy statement about whether rows were written.
   * ⚠️ `reason` exists so the COPY can be accurate about WHY we do not know. One sentence was
   * previously making two different over-claims at once — asserting what the server DID ("accepted
   * and ran the job") and asserting why we could not tell ("its response could not be read") — and
   * it was wrong about the second whenever the body was READABLE but contract-invalid, which is
   * exactly the old-or-incompatible-backend case where an accurate diagnostic matters most.
   */
  | { kind: 'unknown'; stillRunning: boolean; reason: 'http' | 'transport' | 'unreadable' | 'uninformative' };

/** The body shape `routes/mappings.js` returns on 200 (see its res.json at :446). */
export interface PopulateBody {
  success?: boolean;
  wroteNothing?: boolean;
  hasErrors?: boolean;
  totalCreated?: number;
  totalErrors?: number;
  message?: string;
  steps?: Array<{ step: string; created: number; candidates?: number }>;
}

export interface PopulateSignal {
  /** Absent when the request threw before producing a response. */
  status?: number;
  ok?: boolean;
  /** False when a 2xx body could not be parsed — delivery known, outcome NOT known. */
  bodyReadable?: boolean;
  body?: PopulateBody | null;
  thrown?: unknown;
}

export function classifyPopulate(signal: PopulateSignal): PopulateOutcome {
  // 1. A throw. Only an auth refusal is a proven non-execution.
  if (signal.thrown !== undefined) {
    const msg = signal.thrown instanceof Error ? signal.thrown.message : String(signal.thrown);
    if (/unauthoriz/i.test(msg)) {
      return { kind: 'rejected', reason: 'not authorised — refused before the job could start, nothing ran' };
    }
    return { kind: 'unknown', stillRunning: false, reason: 'transport' };
  }

  // 2. Any non-2xx. A status code is not a write receipt: populateAllMappings runs four write stages
  //    with NO transaction (zero BEGIN/COMMIT/ROLLBACK), so a 500 can follow committed rows.
  if (!signal.ok) {
    return { kind: 'unknown', stillRunning: signal.status === 504, reason: 'http' };
  }

  // 3. A 2xx we could not read. Delivery and execution are known; the OUTCOME is not — which is
  //    exactly the case that must not be reported as "the work WAS performed, do not rerun" while
  //    leaving the control live.
  if (signal.bodyReadable === false || !signal.body) {
    return { kind: 'unknown', stillRunning: false, reason: 'unreadable' };
  }

  // 4. A readable 2xx. The backend states the write count directly — but ONLY if it actually stated it.
  //
  // ⛔⛔ THIS LINE WAS `Number(signal.body.totalCreated ?? 0)` AND IT UNDID THE WHOLE INVARIANT.
  // `?? 0` says "the server told me nothing was written". THE SERVER SAID NOTHING AT ALL. Any
  // readable body without the field — `{}`, `{success:true}`, an older server's response — fell
  // through to the no-op branch and RE-ENABLED THE BUTTON.
  // 🔑🔑 A DEFAULT VALUE IS AN ASSERTION MADE ON THE SERVER'S BEHALF. ABSENT IS NOT ZERO, and the
  // coalescing operator is exactly where that distinction goes to die, because it reads as a safety
  // measure. The invariant was correct in the code and defeated in the PARSE — one layer upstream of
  // where I was looking, which is where all four rounds have been.
  const raw = signal.body.totalCreated;
  const stated = typeof raw === 'number' && Number.isFinite(raw) && raw >= 0;

  if (stated && raw > 0) {
    return { kind: 'wrote', totalCreated: raw, clean: signal.body.success === true };
  }

  // ⇒ The ONLY 2xx that may re-enable, and it now requires TWO explicit assertions from the server:
  // a valid count of exactly 0 AND its own `wroteNothing` flag. Either alone is an inference.
  if (stated && raw === 0 && signal.body.wroteNothing === true) {
    return { kind: 'no-op' };
  }

  // Anything else readable but unstated — missing, null, NaN, negative, a string, or a bare
  // `{success:true}` — is UNKNOWN. Unearned means locked.
  return { kind: 'unknown', stillRunning: false, reason: 'uninformative' };
}

/**
 * ⛔ DEFAULT LOCKED. Enumerated as the ENABLED set on purpose: adding a new outcome kind leaves it
 * locked until someone deliberately admits it here, rather than defaulting open the way three
 * successive versions of this file did.
 */
export function locksTheControl(outcome: PopulateOutcome): boolean {
  return !(outcome.kind === 'no-op' || outcome.kind === 'rejected');
}
